/**
 * Options + defaults for the easy browser facade. Sensible defaults wire the
 * whole stack so the caller "just passes bytes": model files served at the site
 * root, and the onnxruntime-web wasm fetched from the matching CDN build (the
 * same string the app's cnn-classifier.ts uses).
 */
import { ImagePixels } from '../core/ports';

/** Custom PDF → pixels renderer (inlined; the pdfjs-v6 facade/decode are dropped in the portal port). */
export type RenderPdf = (bytes: Uint8Array) => Promise<ImagePixels>;

/** onnxruntime-web wasm/JSEP binaries from the matching CDN build. */
export const ORT_WASM_PATHS = 'https://cdn.jsdelivr.net/npm/onnxruntime-web@1.27.0/dist/';

/**
 * Tesseract core to load, pinned to a specific `.wasm.js` file so tesseract.js
 * uses it verbatim instead of auto-selecting a variant from browser feature
 * detection. The default picks the **relaxed-SIMD** core when the browser claims
 * support, but that build is broken in some Firefox releases (leptonica reports
 * "Image file /input cannot be read"). The plain **SIMD** core is the widely-
 * supported, stable choice; the full (non-`-lstm`) build carries both the LSTM
 * engine (OCR) and the legacy engine (OSD orientation), so one path serves every
 * worker. Keep the version in sync with the installed `tesseract.js-core` (same
 * CDN tesseract.js uses by default).
 */
export const TESSERACT_CORE_PATH =
  'https://cdn.jsdelivr.net/npm/tesseract.js-core@7.0.0/tesseract-core-simd.wasm.js';

/** Default model file locations (served from the site root / public/). */
export const DEFAULT_MODEL_URLS = {
  classifier: 'assets/brdocs/doc-cnn.onnx',
  classifierMeta: 'assets/brdocs/doc-cnn.json',
  segmentation: 'assets/brdocs/doc-seg.onnx',
  faceDetector: 'assets/brdocs/yunet.onnx',
} as const;

export interface BrowserOptions {
  /** Override where the ONNX model files are served from. */
  modelUrls?: {
    classifier?: string;
    /** The classifier meta sidecar (defaults to 'doc-cnn.json'). */
    classifierMeta?: string;
    segmentation?: string;
    faceDetector?: string;
  };
  /** Where onnxruntime-web fetches its wasm binaries. Defaults to the ORT CDN. */
  wasmPaths?: string;
  /** Manual MRZ text (for scanDocument's MRZ field mapping). */
  mrzText?: string;
  /** Custom PDF → pixels renderer; when set, pdfjs is never loaded. */
  renderPdf?: RenderPdf;
  /** Execution providers for all ONNX models. Defaults to ['webgpu','wasm']
   * when WebGPU is present, else ['wasm']. */
  executionProviders?: ('webgpu' | 'wasm' | 'cpu')[];
}

/** Pick the default execution providers based on WebGPU availability. */
export function defaultExecutionProviders(): ('webgpu' | 'wasm')[] {
  const webgpu = typeof navigator !== 'undefined' && 'gpu' in navigator;
  return webgpu ? ['webgpu', 'wasm'] : ['wasm'];
}
